'use client';

import React, { useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Select, type SelectOption } from './Select';

interface SelectItemProps {
  value?: string | number;
  children: ReactNode;
  disabled?: boolean;
}

interface SelectGroupProps {
  label: string;
  children: ReactNode;
  disabled?: boolean;
}

// Declarative option data: these components never render browser option elements.
export function SelectItem(_props: SelectItemProps) { return null; }
export function SelectGroup(_props: SelectGroupProps) { return null; }

function optionText(children: ReactNode): string {
  return React.Children.toArray(children).map(child => {
    if (React.isValidElement<{ children?: ReactNode }>(child)) return optionText(child.props.children);
    return typeof child === 'string' || typeof child === 'number' ? String(child) : '';
  }).join('');
}

export function selectFieldOptions(children: ReactNode, group?: string, disabled = false): SelectOption[] {
  return React.Children.toArray(children).flatMap(child => {
    if (!React.isValidElement<SelectItemProps & SelectGroupProps>(child)) return [];
    if (child.type === React.Fragment) return selectFieldOptions(child.props.children, group, disabled);
    if (child.type === SelectGroup) return selectFieldOptions(child.props.children, child.props.label, disabled || !!child.props.disabled);
    if (child.type !== SelectItem) return [];
    return [{ value: String(child.props.value ?? optionText(child.props.children)), label: child.props.children, group, disabled: disabled || !!child.props.disabled }];
  });
}

interface SelectFieldProps {
  children: ReactNode;
  value?: string | number;
  defaultValue?: string | number;
  onValueChange?: (value: string) => void;
  id?: string;
  name?: string;
  form?: string;
  title?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

/** System select with direct values, declarative options and native form submission. */
export function SelectField({ children, value, defaultValue, onValueChange, id, name, form, title, disabled, className, ...aria }: SelectFieldProps) {
  const options = selectFieldOptions(children);
  const [uncontrolledValue, setUncontrolledValue] = useState(() => String(defaultValue ?? options.find(option => !option.disabled)?.value ?? ''));
  const requestedValue = String(value ?? uncontrolledValue);
  const selectedValue = options.some(option => option.value === requestedValue)
    ? requestedValue : options.find(option => !option.disabled)?.value ?? '';

  return <>
    <Select id={id} title={title} disabled={disabled} value={selectedValue} options={options}
      ariaLabel={aria['aria-label']} ariaLabelledBy={aria['aria-labelledby']} ariaDescribedBy={aria['aria-describedby']}
      className="contents" triggerClassName={cn('w-auto', className)}
      onChange={nextValue => { if (value === undefined) setUncontrolledValue(nextValue); onValueChange?.(nextValue); }} />
    {name ? <input type="hidden" name={name} form={form} disabled={disabled} value={selectedValue} /> : null}
  </>;
}
